import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Dimensions
} from 'react-native';
import { theme } from '../constants/theme';
import { X } from '../../assets/icons';

import { Platform } from 'react-native';

// Safe import of IAUUpdateStatus - use shim on web
let IAUUpdateStatus: any;
if (Platform.OS === 'web') {
  // Web doesn't support in-app updates
  IAUUpdateStatus = {
    UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
    INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
    DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
  };
} else {
  try {
    const inAppUpdatesModule = require('react-native-in-app-updates');
    IAUUpdateStatus = inAppUpdatesModule.IAUUpdateStatus || {
      UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
      INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
      DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
    };
  } catch (error) {
    console.warn('react-native-in-app-updates not available in UpdateModal:', error);
    IAUUpdateStatus = {
      UNKNOWN: 0, NOT_AVAILABLE: 1, AVAILABLE: 2, DOWNLOADING: 3, 
      INSTALLING: 4, INSTALLED: 5, FAILED: 6, CANCELED: 7, 
      DOWNLOADED: 8, PENDING: 9, CHECKING: 10, IDLE: 11 
    };
  }
}

type UpdateStatusType = typeof IAUUpdateStatus[keyof typeof IAUUpdateStatus];

interface UpdateModalProps {
  visible: boolean;
  onClose: () => void;
  onUpdate: (type: 'flexible' | 'immediate') => void;
  onInstall: () => void;
  updateStatus: UpdateStatusType;
  isMandatory?: boolean;
  storeVersion?: string;
  currentVersion?: string;
}

const UpdateModal: React.FC<UpdateModalProps> = ({
  visible,
  onClose,
  onUpdate,
  onInstall,
  updateStatus,
  isMandatory = false,
  storeVersion,
  currentVersion
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (updateStatus === IAUUpdateStatus.DOWNLOADING || 
        updateStatus === IAUUpdateStatus.INSTALLING) {
      setIsUpdating(true);
    } else {
      setIsUpdating(false);
    }
  }, [updateStatus]);

  const getStatusText = () => {
    switch (updateStatus) {
      case IAUUpdateStatus.CHECKING:
        return 'Checking for updates...';
      case IAUUpdateStatus.DOWNLOADING:
        return 'Downloading update...';
      case IAUUpdateStatus.INSTALLING:
        return 'Installing update...';
      case IAUUpdateStatus.DOWNLOADED:
        return 'Update ready to install!';
      case IAUUpdateStatus.FAILED:
        return 'Update failed. Please try again.';
      default:
        return 'Update available!';
    }
  };

  const getStatusColor = () => {
    switch (updateStatus) {
      case IAUUpdateStatus.DOWNLOADED:
        return theme.colors.success;
      case IAUUpdateStatus.FAILED:
        return theme.colors.error;
      case IAUUpdateStatus.CHECKING:
      case IAUUpdateStatus.DOWNLOADING:
      case IAUUpdateStatus.INSTALLING:
        return theme.colors.primary;
      default:
        return theme.colors.text;
    }
  };

  const handleFlexibleUpdate = () => {
    onUpdate('flexible');
  };

  const handleImmediateUpdate = () => {
    onUpdate('immediate');
  };

  const handleInstall = () => {
    Alert.alert(
      'Install Update',
      'The update is ready to install. The app will restart after installation.',
      [
        {
          text: 'Install Now',
          onPress: onInstall
        },
        {
          text: 'Cancel',
          style: 'cancel'
        }
      ]
    );
  };

  const handleClose = () => {
    if (!isMandatory && updateStatus !== IAUUpdateStatus.DOWNLOADING) {
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {isMandatory ? 'Update Required' : 'Update Available'}
            </Text>
            {!isMandatory && updateStatus !== IAUUpdateStatus.DOWNLOADING && (
              <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
                <X size={24} color={theme.colors.textLight} />
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.content}>
            <View style={styles.statusContainer}>
              <ActivityIndicator
                size="large"
                color={getStatusColor()}
                animating={isUpdating}
              />
              <Text style={[styles.statusText, { color: getStatusColor() }]}>
                {getStatusText()}
              </Text>
            </View>

            {(storeVersion && currentVersion) && (
              <View style={styles.versionContainer}>
                <Text style={styles.versionText}>
                  Current Version: {currentVersion}
                </Text>
                <Text style={styles.versionText}>
                  Latest Version: {storeVersion}
                </Text>
              </View>
            )}

            <Text style={styles.description}>
              {isMandatory 
                ? 'This update is required to continue using the app. Please update now.'
                : 'A new version of the app is available with bug fixes and improvements.'
              }
            </Text>

            {updateStatus === IAUUpdateStatus.DOWNLOADED && (
              <View style={styles.installContainer}>
                <Text style={styles.installText}>
                  The update has been downloaded and is ready to install. 
                  The app will restart after installation.
                </Text>
              </View>
            )}

            {updateStatus === IAUUpdateStatus.FAILED && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>
                  The update failed to download. You can try again or update manually from the Play Store.
                </Text>
              </View>
            )}
          </View>

          <View style={styles.buttonContainer}>
            {updateStatus === IAUUpdateStatus.UNKNOWN || 
             updateStatus === IAUUpdateStatus.IDLE ? (
              <>
                <TouchableOpacity
                  style={[styles.button, styles.flexibleButton]}
                  onPress={handleFlexibleUpdate}
                  disabled={isUpdating}
                >
                  <Text style={styles.flexibleButtonText}>
                    Update in Background
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.button, styles.immediateButton]}
                  onPress={handleImmediateUpdate}
                  disabled={isUpdating}
                >
                  <Text style={styles.immediateButtonText}>
                    Update Now
                  </Text>
                </TouchableOpacity>
              </>
            ) : updateStatus === IAUUpdateStatus.DOWNLOADED ? (
              <TouchableOpacity
                style={[styles.button, styles.installButton]}
                onPress={handleInstall}
              >
                <Text style={styles.installButtonText}>
                  Install Update
                </Text>
              </TouchableOpacity>
            ) : updateStatus === IAUUpdateStatus.FAILED ? (
              <TouchableOpacity
                style={[styles.button, styles.retryButton]}
                onPress={() => onUpdate('flexible')}
              >
                <Text style={styles.retryButtonText}>
                  Try Again
                </Text>
              </TouchableOpacity>
            ) : null}

            {!isMandatory && updateStatus !== IAUUpdateStatus.DOWNLOADING && (
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={handleClose}
              >
                <Text style={styles.cancelButtonText}>
                  {isMandatory ? 'Exit App' : 'Later'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: width * 0.9,
    maxWidth: 400,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text,
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  content: {
    padding: 20,
  },
  statusContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  statusText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
    textAlign: 'center',
  },
  versionContainer: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  versionText: {
    fontSize: 14,
    color: theme.colors.textLight,
    textAlign: 'center',
    marginBottom: 4,
  },
  description: {
    fontSize: 14,
    color: theme.colors.textLight,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 16,
  },
  installContainer: {
    backgroundColor: theme.colors.success,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  installText: {
    fontSize: 14,
    color: theme.colors.success,
    textAlign: 'center',
  },
  errorContainer: {
    backgroundColor: theme.colors.error,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    color: theme.colors.error,
    textAlign: 'center',
  },
  buttonContainer: {
    padding: 20,
    paddingTop: 0,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  flexibleButton: {
    backgroundColor: theme.colors.primaryLight,
  },
  flexibleButtonText: {
    color: theme.colors.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  immediateButton: {
    backgroundColor: theme.colors.primary,
  },
  immediateButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  installButton: {
    backgroundColor: theme.colors.success,
  },
  installButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  retryButton: {
    backgroundColor: theme.colors.warning,
  },
  retryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.colors.textLight,
  },
  cancelButtonText: {
    color: theme.colors.textLight,
    fontSize: 16,
    fontWeight: '500',
  },
});

export default UpdateModal;
